/**
 * This component will show the booking info and calculated total price.
 * I.e. dates and other details related to payment decision in receipt format.
 */
import React from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  DATE_TYPE_DATE,
  DATE_TYPE_DATETIME,
  DATE_TYPE_TIME,
  LINE_ITEM_CUSTOMER_COMMISSION,
  LINE_ITEM_FIXED,
  LINE_ITEM_HOUR,
  LINE_ITEM_PROVIDER_COMMISSION,
  LISTING_UNIT_TYPES,
  propTypes,
} from '../../util/types';

const { Money } = sdkTypes;

import LineItemBookingPeriod from './LineItemBookingPeriod';
import LineItemBasePriceMaybe from './LineItemBasePriceMaybe';
import LineItemSubTotalMaybe from './LineItemSubTotalMaybe';
import LineItemShippingFeeMaybe from './LineItemShippingFeeMaybe';
import LineItemPickupFeeMaybe from './LineItemPickupFeeMaybe';
import LineItemCustomerCommissionMaybe from './LineItemCustomerCommissionMaybe';
import LineItemCustomerCommissionRefundMaybe from './LineItemCustomerCommissionRefundMaybe';
import LineItemProviderCommissionMaybe from './LineItemProviderCommissionMaybe';
import LineItemProviderCommissionRefundMaybe from './LineItemProviderCommissionRefundMaybe';
import LineItemRefundMaybe from './LineItemRefundMaybe';
import LineItemTotalPrice from './LineItemTotalPrice';
import LineItemUnknownItemsMaybe from './LineItemUnknownItemsMaybe';
import LineItemTieredPricingMaybe from './LineItemTieredPricingMaybe';
import LineItemCustomTotalPrice from './LineItemCustomTotalPrice';
import LineItemSplitPayment from './LineItemSplitPayment';

import css from './OrderBreakdown.module.css';

/**
 * Subscription-aware breakdown that matches the checkout page structure.
 * Splits line items into "First payment" and "Future charges" sections.
 */
const SubscriptionBreakdown = ({
  lineItems,
  booking,
  lineItemUnitType,
  dateType,
  timeZone,
  currency,
  intl,
  isCustomer,
  isProvider,
  marketplaceName,
  listing,
  sharePercent,
  securityInFirstPayment,
}) => {
  // When viewing as a share participant, scale amounts by their percentage
  const applyShare = amount => sharePercent != null ? Math.floor((amount * sharePercent) / 100) : amount;
  const monthlyItem = lineItems.find(
    item => item.code === 'line-item/monthly' && !item.reversal
  );
  const securityDepositItem = lineItems.find(
    item => item.code === 'line-item/security-deposit' && !item.reversal
  );
  const cleaningFeeItem = lineItems.find(
    item => item.code === 'line-item/cleaning-fee' && !item.reversal
  );
  const nonRefundableItem = lineItems.find(
    item => item.code === 'line-item/non-refundable-fee' && !item.reversal
  );
  const commissionItem = lineItems.find(
    item =>
      (item.code === LINE_ITEM_CUSTOMER_COMMISSION || item.code === LINE_ITEM_PROVIDER_COMMISSION) &&
      !item.reversal
  );

  const dayItem = lineItems.find(item => item.code === 'line-item/day' && !item.reversal);
  const weeklyPartialItem = lineItems.find(item => item.code === 'line-item/weekly' && !item.reversal);

  const bookingMonths = parseInt(monthlyItem?.quantity, 10) || 1;
  const monthlyTotalAmount = monthlyItem?.lineTotal?.amount || 0;
  const perMonthAmount = Math.round(monthlyTotalAmount / bookingMonths);

  // Partial period (days/weeks remainder at end of booking) is charged DEFERRED —
  // on the date the partial period starts (bookingStart + fullMonths), NOT upfront.
  const partialDaysAmount = dayItem?.lineTotal?.amount || 0;
  const partialWeeksAmount = weeklyPartialItem?.lineTotal?.amount || 0;
  const partialPeriodAmount = partialDaysAmount + partialWeeksAmount;

  // Date when the partial period charge will be triggered (startDate + fullMonths)
  const partialChargeDate = (() => {
    const bookingStart = booking?.attributes?.start;
    if (!bookingStart || !partialPeriodAmount) return null;
    const d = new Date(bookingStart);
    d.setUTCMonth(d.getUTCMonth() + bookingMonths);
    d.setUTCHours(0, 0, 0, 0);
    return d;
  })();

  const priceSettings = listing?.attributes?.publicData?.priceSettings || {};
  const securityDepositMonths = priceSettings?.securityDepositMonths || 2;
  const securityDepositWeeks = priceSettings?.securityDepositWeeks || 2;

  // First payment: 1 month rent + cleaning fee + non-refundable fee
  // Partial period days are charged DEFERRED on startDate + fullMonths, not here.
  // When securityInFirstPayment is true (booking < 30 days), include security deposit too.
  // For providers, also include commission (negative amount = deducted from payout).
  const firstPaymentAmount =
    perMonthAmount +
    (cleaningFeeItem?.lineTotal?.amount || 0) +
    (nonRefundableItem?.lineTotal?.amount || 0) +
    (securityInFirstPayment && securityDepositItem ? (securityDepositItem.lineTotal?.amount || 0) : 0) +
    (isProvider && commissionItem ? (commissionItem.lineTotal?.amount || 0) : 0);

  const securityDepositAmount = securityDepositItem?.lineTotal?.amount || 0;

  const fmtPerMonth = formatMoney(intl, new Money(applyShare(perMonthAmount), currency));
  const fmtCleaningFee = cleaningFeeItem
    ? formatMoney(intl, new Money(applyShare(cleaningFeeItem.lineTotal.amount), currency))
    : null;
  const fmtNonRefundable = nonRefundableItem
    ? formatMoney(intl, new Money(applyShare(nonRefundableItem.lineTotal.amount), currency))
    : null;
  const fmtCommission = commissionItem
    ? formatMoney(intl, new Money(applyShare(commissionItem.lineTotal.amount), currency))
    : null;
  const fmtFirstPayment = formatMoney(intl, new Money(applyShare(firstPaymentAmount), currency));
  const fmtSecurityDeposit = securityDepositItem
    ? formatMoney(intl, new Money(applyShare(securityDepositItem.lineTotal.amount), currency))
    : null;
  const fmtMonthlySubscription = formatMoney(intl, new Money(applyShare(perMonthAmount), currency));

  return (
    <>
      <LineItemBookingPeriod
        booking={booking}
        code={lineItemUnitType}
        dateType={dateType}
        timeZone={timeZone}
      />

      {sharePercent != null && (
        <div className={css.lineItem} style={{ marginBottom: '8px' }}>
          <span className={css.itemLabel} style={{ fontStyle: 'italic' }}>
            Your share: {sharePercent}%
          </span>
        </div>
      )}

      <div className={css.subscriptionSection}>
        <h4 className={css.subscriptionSectionTitle}>
          <FormattedMessage id="CheckoutPage.firstPaymentTitle" />
        </h4>
        <div className={css.lineItem}>
          <span className={css.itemLabel}>
            <FormattedMessage id="CheckoutPage.monthlyRent" />
          </span>
          <span className={css.itemValue}>{fmtPerMonth}</span>
        </div>
        {fmtCleaningFee && (
          <div className={css.lineItem}>
            <span className={css.itemLabel}>
              <FormattedMessage id="CheckoutPage.cleaningFee" />
            </span>
            <span className={css.itemValue}>{fmtCleaningFee}</span>
          </div>
        )}
        {fmtNonRefundable && (
          <div className={css.lineItem}>
            <span className={css.itemLabel}>
              <FormattedMessage id="CheckoutPage.nonRefundableFee" />
            </span>
            <span className={css.itemValue}>{fmtNonRefundable}</span>
          </div>
        )}
        {securityInFirstPayment && fmtSecurityDeposit && (
          <div className={css.lineItem}>
            <span className={css.itemLabel}>
              <FormattedMessage
                id="CheckoutPage.securityDeposit"
                values={{ months: String(securityDepositMonths) }}
              />
            </span>
            <span className={css.itemValue}>{fmtSecurityDeposit}</span>
          </div>
        )}
        {isProvider && fmtCommission && (
          <div className={css.lineItem}>
            <span className={css.itemLabel}>
              <FormattedMessage
                id="OrderBreakdown.commissionFeeLabel"
                values={{ marketplaceName }}
              />
            </span>
            <span className={css.itemValue}>{fmtCommission}</span>
          </div>
        )}
        <hr className={css.totalDivider} />
        <div className={css.lineItemTotal}>
          <div className={css.totalLabel}>
            <FormattedMessage
              id={isProvider ? 'OrderBreakdown.firstPaymentTotalProvider' : 'OrderBreakdown.firstPaymentTotal'}
            />
          </div>
          <div className={css.totalPrice}>{fmtFirstPayment}</div>
        </div>
        {isProvider && fmtCommission && (
          <span className={css.feeInfo}>
            <FormattedMessage id="OrderBreakdown.commissionFeeNote" />
          </span>
        )}
      </div>

      {((!securityInFirstPayment && securityDepositAmount > 0) || bookingMonths > 1 || partialPeriodAmount > 0) && (
        <div className={css.subscriptionSection}>
          <h4 className={css.subscriptionSectionTitle}>
            <FormattedMessage id="CheckoutPage.futureChargesTitle" />
          </h4>
          {partialPeriodAmount > 0 && (
            <div className={css.lineItem}>
              <span className={css.depositLabel}>
                <FormattedMessage
                  id="OrderBreakdown.partialPeriod"
                  values={{
                    days: dayItem?.quantity || 0,
                    weeks: weeklyPartialItem?.quantity || 0,
                  }}
                />
                {partialChargeDate && (
                  <span className={css.depositTiming}>
                    <FormattedMessage
                      id="OrderBreakdown.partialPeriodDate"
                      values={{
                        date: intl.formatDate(partialChargeDate, { month: 'long', day: 'numeric', year: 'numeric' }),
                      }}
                    />
                  </span>
                )}
              </span>
              <span className={css.itemValue}>
                {formatMoney(intl, new Money(applyShare(partialPeriodAmount), currency))}
              </span>
            </div>
          )}
          {!securityInFirstPayment && securityDepositAmount > 0 && (
            <div className={css.lineItem}>
              <span className={css.depositLabel}>
                <FormattedMessage
                  id="CheckoutPage.securityDeposit"
                  values={{ months: String(securityDepositMonths) }}
                />
                <span className={css.depositTiming}>
                  <FormattedMessage
                    id="CheckoutPage.securityDepositTiming"
                    values={{ weeks: String(securityDepositWeeks) }}
                  />
                </span>
              </span>
              <span className={css.itemValue}>{fmtSecurityDeposit}</span>
            </div>
          )}
          {bookingMonths > 1 && (
            <>
              <span className={css.rentDivider}>Rent</span>
              {Array.from({ length: bookingMonths - 1 }, (_, i) => i + 2).map(month => {
                const bookingStart = booking?.attributes?.start;
                const monthDate = bookingStart ? new Date(bookingStart) : null;
                if (monthDate) monthDate.setMonth(monthDate.getMonth() + (month - 1));
                const monthLabel = monthDate
                  ? intl.formatDate(monthDate, { month: 'long', year: 'numeric' })
                  : intl.formatMessage({ id: 'CheckoutPage.monthlyRentMonth' }, { month: String(month) });
                return (
                  <div key={month} className={css.receiptRow}>
                    <span>{monthLabel}</span>
                    <span className={css.receiptDots} />
                    <span>{fmtMonthlySubscription}</span>
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}

    </>
  );
};

export const OrderBreakdownComponent = props => {
  const {
    rootClassName,
    className,
    userRole,
    transaction,
    booking,
    timeZone,
    currency,
    selectedRentalType,
    splitPayment,
    guests,
    marketplaceName,
    intl,
    listing,
    sharePercent,
    securityInFirstPayment,
  } = props;

  const isCustomer = userRole === 'customer';
  const isProvider = userRole === 'provider';
  const allLineItems = transaction?.attributes?.lineItems || [];
  // We'll show only line-items that are specific for the current userRole (customer vs provider)
  const lineItems = allLineItems.filter(lineItem => lineItem.includeFor?.includes(userRole));

  // Detect if this is a subscription-style booking (has monthly + security deposit line items)
  const hasMonthlyItem = lineItems.some(
    item => item.code === 'line-item/monthly' && !item.reversal
  );
  const hasSecurityDeposit = lineItems.some(
    item => item.code === 'line-item/security-deposit' && !item.reversal
  );
  const isSubscriptionBooking = hasMonthlyItem && (hasSecurityDeposit || hasMonthlyItem);

  // Detect if tiered pricing is in use (monthly/weekly/day breakdown)
  const hasTieredPricing = lineItems.some(
    item => ['line-item/monthly', 'line-item/weekly'].includes(item.code) && !item.reversal
  );

  const unitLineItem = lineItems.find(
    item => LISTING_UNIT_TYPES.includes(item.code) && !item.reversal
  );
  // Line-item code that matches with base unit: day, night, hour, fixed, item
  const lineItemUnitType = unitLineItem?.code;
  const dateType = [LINE_ITEM_HOUR, LINE_ITEM_FIXED].includes(lineItemUnitType)
    ? DATE_TYPE_DATETIME
    : DATE_TYPE_DATE;

  const hasCommissionLineItem = lineItems.find(item => {
    const hasCustomerCommission = isCustomer && item.code === LINE_ITEM_CUSTOMER_COMMISSION;
    const hasProviderCommission = isProvider && item.code === LINE_ITEM_PROVIDER_COMMISSION;
    return (hasCustomerCommission || hasProviderCommission) && !item.reversal;
  });

  const classes = classNames(rootClassName || css.root, className);

  const participants = Math.max(1, parseInt(guests, 10) || 1);

  // Calculate total from line items for split payment (customer view)
  // Include all items including customer commission, exclude only reversals
  const totalFromLineItems = lineItems.reduce((sum, item) => {
    if (item.reversal) {
      return sum;
    }
    return sum + (item.lineTotal?.amount || 0);
  }, 0);

  const splitShares =
    splitPayment && participants > 1 && totalFromLineItems > 0
      ? Array.from({ length: participants }).map((_, i) => {
          const base = Math.floor(totalFromLineItems / participants);
          const remainder = totalFromLineItems - base * participants;

          const partAmount = base + (i === 0 ? remainder : 0);

          return {
            index: i,
            amount: { amount: partAmount, currency },
          };
        })
      : [];

  // For subscription bookings, use the checkout-style breakdown for both customer and provider
  if (isSubscriptionBooking) {
    return (
      <div className={classes}>
        <SubscriptionBreakdown
          lineItems={lineItems}
          booking={booking}
          lineItemUnitType={lineItemUnitType}
          dateType={dateType}
          timeZone={timeZone}
          currency={currency}
          intl={intl}
          isCustomer={isCustomer}
          isProvider={isProvider}
          marketplaceName={marketplaceName}
          listing={listing}
          sharePercent={sharePercent}
          securityInFirstPayment={securityInFirstPayment}
        />
      </div>
    );
  }

  return (
    <div className={classes}>
      <LineItemBookingPeriod
        booking={booking}
        code={lineItemUnitType}
        dateType={dateType}
        timeZone={timeZone}
      />

      {selectedRentalType === 'daily' && !hasTieredPricing && (
        <LineItemBasePriceMaybe lineItems={lineItems} code={lineItemUnitType} intl={intl} />
      )}

      <LineItemShippingFeeMaybe lineItems={lineItems} intl={intl} />
      <LineItemPickupFeeMaybe lineItems={lineItems} intl={intl} />
      <LineItemTieredPricingMaybe lineItems={lineItems} isProvider={isProvider} intl={intl} />
      <LineItemUnknownItemsMaybe lineItems={lineItems} isProvider={isProvider} intl={intl} />

      <LineItemSubTotalMaybe
        lineItems={lineItems}
        code={lineItemUnitType}
        userRole={userRole}
        intl={intl}
        marketplaceCurrency={currency}
      />
      <LineItemRefundMaybe lineItems={lineItems} intl={intl} marketplaceCurrency={currency} />

      <LineItemCustomerCommissionMaybe
        lineItems={lineItems}
        isCustomer={isCustomer}
        marketplaceName={marketplaceName}
        intl={intl}
      />
      <LineItemCustomerCommissionRefundMaybe
        lineItems={lineItems}
        isCustomer={isCustomer}
        marketplaceName={marketplaceName}
        intl={intl}
      />

      <LineItemProviderCommissionMaybe
        lineItems={lineItems}
        isProvider={isProvider}
        marketplaceName={marketplaceName}
        intl={intl}
      />
      <LineItemProviderCommissionRefundMaybe
        lineItems={lineItems}
        isProvider={isProvider}
        marketplaceName={marketplaceName}
        intl={intl}
      />

      {splitShares.length ? (
        <>
          <hr className={css.totalDivider} />
          {splitShares.map(share => (
            <LineItemSplitPayment
              key={`split-${share.index}`}
              intl={intl}
              share={share}
              labelId="OrderBreakdown.splitPaymentParticipant"
            />
          ))}
          <span className={css.splitPaymentAdvice}>Don't worry — you can edit the amounts at checkout.</span>
        </>
      ) : null}

      {/* <LineItemTotalPrice transaction={transaction} isProvider={isProvider} intl={intl} /> */}
      <LineItemCustomTotalPrice
        lineItems={lineItems}
        currency={currency}
        intl={intl}
        labelId="OrderBreakdown.totalPrice"
      />

      {hasCommissionLineItem ? (
        <span className={css.feeInfo}>
          <FormattedMessage id="OrderBreakdown.commissionFeeNote" />
        </span>
      ) : null}
    </div>
  );
};

/**
 * Order breakdown aka receipt
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to component's own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string} props.marketplaceName
 * @param {string} props.timeZone IANA time zone name
 * @param {string} props.currency E.g. 'USD'
 * @param {'customer' | 'provider'} props.userRole
 * @param {propTypes.transaction} props.transaction
 * @param {propTypes.booking?} props.booking
 * @param {DATE_TYPE_DATE | DATE_TYPE_TIME | DATE_TYPE_DATETIME} props.dateType
 * @param {Object?} props.listing - The listing object (for subscription breakdown)
 * @returns {JSX.Element} the order breakdown component
 */
const OrderBreakdown = props => {
  const intl = useIntl();
  return <OrderBreakdownComponent intl={intl} {...props} />;
};

export default OrderBreakdown;
